import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { AxiosResponse } from 'axios';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { PasswordFormField } from 'lib/form/PasswordFormField';
import { TextFormField } from 'lib/form/TextFormField';
import logger from 'lib/logger';
import { signIn } from 'store/auth/authSlice';
import { resetRewards } from 'store/basket/basketSlice';
import { useAppDispatch } from 'store/hooks';
import { LoadingButton } from 'ui/LoadingButton';
import { userApi } from './userApi';
import { UserData } from './model/User';
import { useSnackbar } from 'notistack';
import { isServerError } from 'src/utils/request';

export interface TwoFactorFormProps {
    identity: string;
    noPadding?: boolean;
    onSuccess?: (res: UserData) => void;
}

export const TwoFactorForm: React.FC<TwoFactorFormProps> = ({ identity, noPadding = false, onSuccess }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [errorMessage, setErrorMessage] = React.useState('');

    React.useEffect(() => {
        if (!identity) {
            return;
        }
        userApi.requestFor2FA(identity).catch(e => {
            logger.error(e);
            if (isServerError(e?.status)) {
                enqueueSnackbar(e?.data?.message || t('GENERAL_ERROR_PROCESSING_REQUEST'), {
                    variant: 'error'
                });
            }
        });
    }, [enqueueSnackbar, identity, t]);

    const validation = React.useMemo(
        () =>
            Yup.object().shape({
                token: Yup.string().required(t('FORMS_VALIDATION_REQUIRED')),
                password: Yup.string().required(t('FORMS_VALIDATION_REQUIRED'))
            }),
        [t]
    );

    const handleSubmit = React.useCallback(
        ({ token, password }: { token: string; password: string }) => {
            const credentials = btoa(`${identity}:${password}`);
            return dispatch(signIn({ credentials, token }))
                .unwrap()
                .then(res => {
                    dispatch(resetRewards());
                    onSuccess?.(res);
                    return res;
                })
                .catch((error: AxiosResponse<{ message: string; code: string }>) => {
                    let errorMsg: string;
                    switch (error.status) {
                        case 401:
                            errorMsg =
                                error.data?.code === 'InvalidCredentials'
                                    ? "Sorry! We couldn't find you with that email and password combo."
                                    : t('DIALOG_RESET_UNAUTHORISED_MESSAGE');
                            break;
                        default:
                            errorMsg = t('GENERAL_ERROR_PROCESSING_REQUEST');
                    }
                    if (error.data?.code === 'E-WAF-0001') {
                        errorMsg = error.data.message;
                    }
                    setErrorMessage(errorMsg);
                });
        },
        [dispatch, identity, onSuccess, t]
    );

    return (
        <Formik
            key={identity}
            initialValues={{ token: '', password: '' }}
            validationSchema={validation}
            onSubmit={handleSubmit}
        >
            {({ submitForm, isValid, isSubmitting, values: { token, password } }) => (
                <Form>
                    <Grid container spacing={2} sx={{ px: noPadding ? 0 : 2 }}>
                        <Grid size={12}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {t('ONBOARDING_SIGNIN_VERIFY_MESSAGE', { credential: identity })}
                            </Typography>
                        </Grid>
                        <Grid size={12}>
                            <TextFormField
                                name="token"
                                label={t('ONBOARDING_SIGNIN_VERIFY_CODE')}
                                InputProps={{ autoComplete: 'off' }}
                            />
                        </Grid>
                        <Grid size={12}>
                            <PasswordFormField name="password" label={t('ONBOARDING_SIGNIN_PASSWORD')} />
                        </Grid>
                        {!!errorMessage && (
                            <Grid size={12}>
                                <Typography color="error">{errorMessage}</Typography>
                            </Grid>
                        )}
                        <Grid size={12}>
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                disabled={!isValid || isSubmitting || !token || !password}
                                fullWidth
                                onClick={submitForm}
                                loading={isSubmitting}
                            >
                                {t('AUTH_FORM_LOGIN')}
                            </LoadingButton>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    );
};
